import './CompanyEmail.css';
import { useEffect, useState } from 'react';
import { getUserCompany, getUserProfile, validateWorkEmail, createWorkEmailValidation, checkWorkEmailValidation, refreshToken } from '../../utils/api';
import FadeIn from '../../utils/fadein';
import queryString from 'query-string';
import { Link, Routes, Route, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Building2, AlertCircle, CheckCircle, ArrowRight, Loader2, MailCheck } from 'lucide-react';
import { FaClock, FaUser, FaEdit, FaListOl, FaCreditCard, FaRegEnvelopeOpen, FaUsers, FaRegCalendarAlt, FaRegEdit, FaRegCalendarCheck, FaRegCalendarTimes, FaRegCircle } from "react-icons/fa";
import Spinner from '../../components/layout/Spinner';


const CompanyEmail = ({ userType }) => {
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [token, setToken] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [email, setEmail] = useState('');
  const [verification, setVerification] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadCompany() {
      let res = await getUserCompany();

      if (res.success) {
        setCompany(res.data);
        setCompanyLoaded(true);
      }

      else {
        window.location.href = '/account/company';
      }
    }

    async function loadUserProfile() {
      let res = await getUserProfile();

      if (res.success) {
        setUserProfile(res.data);
        if (res.data.work_email_verified === true) {
          setIsVerified(true);
        }

        else {
          setIsVerified(false);
        }
      }

      else if (res.status === 403) {
        let _refresh = await refreshToken();

        if (_refresh.success) {
          localStorage.setItem('access_token', _refresh.data.accessToken);
          localStorage.setItem('refresh_token', _refresh.data.refreshToken);

          let res = await getUserProfile();

          if (res.success) {
            setUserProfile(res.data);
            if (res.data.work_email_verified === true) {
              setIsVerified(true);
            }

            else {
              setIsVerified(false);
            }
          }
        }
      }
    }

    async function checkToken() {
      const parsed = queryString.parse(window.location.search);
      let _token = '';

      if (parsed["token"]) {
        _token = parsed["token"];
        setToken(_token);
        setHasToken(true);

        let res = await checkWorkEmailValidation(_token);

        if (res.success) {
          if (res.data === true) {
            setIsVerified(true);
          }

          else {
            alert('verification token is invalid');
          }
        }
      }
    }

    loadCompany();
    loadUserProfile();
    checkToken();
  }, []);

  async function handleSend() {
    if (email.length > 0) {
      let res = await createWorkEmailValidation(company.website, email, company.company_id);

      if (res.success) {
        if (res.data === true) {
          setIsSent(true);
        }

        else {
          alert(res.message);
        }
      }

      else if (res.status === 403) {
        let _refresh = await refreshToken();

        if (_refresh.success) {
          localStorage.setItem('access_token', _refresh.data.accessToken);
          localStorage.setItem('refresh_token', _refresh.data.refreshToken);

          let res = await createWorkEmailValidation(company.website, email, company.company_id);

          if (res.success) {
            if (res.data === true) {
              setIsSent(true);
            }

            else {
              alert(res.message);
            }
          }
        }
      }
    }

    else {
      alert('Must enter a valid work email');
    }
  }

  return (
    <div className=' p-6'>
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Verify Work Email</h1>

      {isSent === false && company.website && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-blue-700 text-sm">
              Your website's domain must match your work email's domain to be verified.
            </p>
          </div>

          {isVerified === false && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <Building2 className="h-5 w-5 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Company Website</div>
                  <div className="font-medium text-gray-900">{company.website}</div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="txtEmail" className="block text-sm font-medium text-gray-700">
                  Enter Your Work Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    id="txtEmail"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="your.name@company.com"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <button
                onClick={() => handleSend()}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                <MailCheck className="h-5 w-5" />
                Send Verification Code
              </button>

              <div className="flex gap-2 text-sm text-gray-600 bg-yellow-50 p-4 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <p>
                  If you cannot verify your work email, please{" "}
                  <a href="/leave-feedback" className="text-purple-600 hover:text-purple-700 font-medium">
                    reach out to our support team
                  </a>{" "}
                  and we will help get you verified as quickly as possible!
                </p>
              </div>
            </div>
          )}

          {isVerified === true && (
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  Your work email is verified!
                </h2>
                <p className="text-gray-600">You now have full access to all features.</p>
              </div>

              <button
                onClick={() => navigate('/dashboard/buyer')}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                Go to Dashboard <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {isSent === false && !company.website && companyLoaded && (
        <div className="space-y-6 text-center">
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-6">
            <AlertCircle className="h-8 w-8 text-yellow-500 mx-auto mb-4" />
            <p className="text-gray-800 mb-4">
              You must have a valid company website to verify your work email.
            </p>
            <a 
              href="/account/company#website"
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
            >
              Edit Company Profile
            </a>
          </div>
        </div>
      )}

      {isSent === true && (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100">
            <Mail className="h-8 w-8 text-blue-600" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">Check Your Inbox!</h2>
            <p className="text-gray-600">
              We've sent you a verification email. Please check your inbox and follow the instructions.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompanyEmail;